import {
  formatToMaxFourDigits,
  getSavedLang,
} from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import { resolveKpiContext } from './kpi-context.util';
import { RIGHT_DRAWER } from '@flexo/reducers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  checkItIsToday,
  formatDay,
  formatMonth,
  formatWeek,
  formatYear,
} from '../../../../../organisms/src/lib/hive-calendar/helpers';
import styles from './right-drawer-content.module.scss';

interface DrawerConfig {
  titleKey: string;
  descriptionKey: string;
  listTitleKey: string;
  listTitleSavingsKey: string;
}

interface TotalEstimatedBenefitsDrawerContentProps {
  drawerConfig: DrawerConfig;
}

const ENV = (import.meta as any).env;

const savedLang = getSavedLang() || null;
const browserLanguage =
  savedLang?.split('_')[1] ||
  navigator.language.split('-')[0] ||
  ENV.VITE_APP_LANGUAGE;

export const TotalEstimatedBenefitsDrawerContent: React.FC<
  TotalEstimatedBenefitsDrawerContentProps
> = ({ drawerConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const location = useLocation();

  const pathSegment = location.pathname.split('/')[1];

  const {
    hiveCalendar: { selectedDate, selectedTimeSet },
  } = calendar;
  let entityType = 'site';

  if (pathSegment?.includes('member')) {
    entityType = 'member';
  } else if (pathSegment?.includes('community')) {
    entityType = 'community';
  }

  // Configurazione per tipo
  const config: any = {
    community: {
      getName: (entity: any) => entity?.communityName,
      entity: 'Community',
    },
    member: {
      getName: (entity: any) =>
        `${entity?.memberFirstName || ''} ${
          entity?.memberLastName || ''
        }`.trim(),
      entity: 'Member',
    },
    site: {
      // FLEXO-6133 #6d: friendly site name first — end-users don't know their EIC.
      getName: (entity: any) => entity?.siteName || entity?.pod,
      entity: 'Site',
    },
  }[entityType];

  const kpiContext = resolveKpiContext(drawerConfig as any, pathSegment);

  const overviewKpiApiData =
    (kpi[`${kpiContext}${config.entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const customKpiData = kpi[`${kpiContext}CustomKpi${config.entity}`] || null;
  const entity = metadata?.selectedEntity || {};

  function getKpi(kpiName: string) {
    const kpi =
      (overviewKpiApiData || []).find((kpi: any) => kpi.name === kpiName) ||
      null;
    if (!kpi) return '-.-';
    return `${kpi.unit} ${formatToMaxFourDigits(
      kpi.value,
      kpiContext === 'Economics'
    )}`;
  }

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  const getDate = () => {
    let result = '';
    switch (selectedTimeSet) {
      case 'day':
        if (checkItIsToday(selectedDate)) result = t('widgets.calendar.today');
        else result = formatDay(selectedDate);
        break;
      case 'week':
        result = selectedDate[0]
          ? `${formatWeek(selectedDate)}`
          : 'Select Week';
        break;
      case 'month':
        result = formatMonth(selectedDate);
        break;
      case 'year':
        result = formatYear(selectedDate);
        break;
      default:
        if (
          formatDay(selectedDate) === formatDay([new Date(), new Date()] as any)
        )
          result = t('widgets.calendar.yesterday');
        else result = formatDay(selectedDate);
        break;
    }
    return result;
  };

  return (
    <div className={styles.RightDrawerContent__Wrapper}>
      <div className={styles.RightDrawerContent__Title}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {t(drawerConfig.titleKey)}
        </p>
        <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
      </div>
      <div className={styles.RightDrawerContent__Subtitle}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {config.getName(entity)} <label>{getDate()}</label>
        </p>
      </div>
      <div className={styles.RightDrawerContent__Body}>
        <p className={styles.RightDrawerContent__Body__Value}>
          {getKpi('total_benefits')}
        </p>
        <p className={styles.RightDrawerContent__Body__Description}>
          {t(drawerConfig.descriptionKey)}
        </p>
        {/* Ricavi */}
        {getKpi('revenues') !== '' &&
          getKpi('revenues') !== '-.-' &&
          getKpi('revenues') !== '€ null' &&
          getKpi('revenues') !== '€ -.-' && (
            <>
              <p className={styles.RightDrawerContent__Body__List__Title}>
                {getKpi('revenues')} {t(drawerConfig.listTitleKey)}
              </p>
              <ul className={styles.RightDrawerContent__Body__List}>
                {(customKpiData?.[0]?.kpi || [])
                  ?.filter((kpi: any) => kpi.type === 'revenue')
                  .map((kpi: any, kpi_index: number) => (
                    <li
                      className={styles.RightDrawerContent__Body__List__Item}
                      key={`benefit_revenue_${kpi_index}_${kpi.name}`}
                    >
                      <p className={styles.RightDrawerContent__Paragraph}>
                        {kpi.label}
                      </p>
                      <p className={styles.RightDrawerContent__Paragraph}>
                        {kpiContext === 'Economics'
                          ? `${kpi.unit} ${formatToMaxFourDigits(
                              kpi.value,
                              true
                            )}`
                          : `${formatToMaxFourDigits(kpi.value)} ${kpi.unit}`}
                      </p>
                    </li>
                  ))}
              </ul>
            </>
          )}
        {/* Risparmi */}
        {/* {(getKpi('savings') !== '' && getKpi('savings') !== '€ null' && getKpi('savings') !== '€ -.-') && (
          <>
            <br />
            <p className={styles.RightDrawerContent__Body__List__Title}>
              {getKpi('savings')} {t(drawerConfig.listTitleSavingsKey)}
            </p>
            <ul className={styles.RightDrawerContent__Body__List}>
              {(customKpiData?.[0]?.kpi || [])
                // ?.filter((kpi: any) => )
                .map((kpi: any, kpi_index: number) => (
                  <li className={styles.RightDrawerContent__Body__List__Item} key={`benefit_saving_${kpi_index}_${kpi.name}`}>
                    <p className={styles.RightDrawerContent__Paragraph}>{kpi.label}</p>
                    <p className={styles.RightDrawerContent__Paragraph}>
                      {kpiContext === 'Economics' && `${kpi.unit} `}{formatToMaxFourDigits(kpi.value)}{kpiContext !== 'Economics' && ` ${kpi.unit}`}
                    </p>
                  </li>
                ))}
            </ul>
          </>
        )} */}
      </div>
    </div>
  );
};
